import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ccApi, farmbaseApi } from '../../services/api';
import { useTargetStore } from '../../store/targetStore';
import { useUnitStore } from '../../store/unitStore';
import type { Farmbase } from '../../types/domain';

// §19.2 response shapes (the authoritative rollup — includes global targets).
interface FriendlyUnit {
  id: string;
  code: string;
  name?: string;
  totals: { officers: number; jcos: number; ors: number; total: number };
}
interface DispositionResponse {
  friendly: { units: FriendlyUnit[]; totals: { officers: number; jcos: number; ors: number; total: number } };
  enemy: { totals: { total: number; eliminated: number; remaining: number } };
}

// Friendly strength (farmbase O/JCO/OR rolled up per unit) vs enemy manpower (targets).
// Prefers the server's /force-disposition (§19.2); falls back to a client-side rollup
// so the panel still renders if the endpoint is briefly unavailable.
export default function ForceDisposition() {
  const { units } = useUnitStore();
  const { targets } = useTargetStore();

  const dispoQ = useQuery({
    queryKey: ['force-disposition'],
    queryFn: async () => (await ccApi.getForceDisposition()).data as DispositionResponse,
    retry: false,
    refetchInterval: 30000,
  });

  // Fallback rollup from farmbases + targets store (used only if the endpoint fails).
  const farmbasesQ = useQuery({
    queryKey: ['disposition-farmbases'],
    queryFn: async () => (await farmbaseApi.getAll()).data.farmbases as Farmbase[],
    enabled: dispoQ.isError,
    refetchInterval: 30000,
  });

  const { rows, friendlyTotal, enemy } = useMemo(() => {
    // Preferred path — authoritative endpoint (defensively read; a partial payload must not throw).
    if (dispoQ.data?.friendly?.units) {
      const f = dispoQ.data.friendly;
      const rows = f.units.map((u) => ({
        unitId: u.id,
        code: u.code ?? u.id?.slice(0, 6) ?? '—',
        officers: u.totals?.officers ?? 0,
        jcos: u.totals?.jcos ?? 0,
        ors: u.totals?.ors ?? 0,
        friendly: u.totals?.total ?? 0,
      }));
      const friendlyTotal = f.totals ?? { officers: 0, jcos: 0, ors: 0, total: 0 };
      const enemy = dispoQ.data.enemy?.totals ?? { total: 0, eliminated: 0, remaining: 0 };
      return { rows, friendlyTotal, enemy };
    }

    // Fallback path — recompute from farmbases + targets.
    const fbs = farmbasesQ.data ?? [];
    const byUnit = new Map<string, { officers: number; jcos: number; ors: number }>();
    const ensure = (uid: string) => {
      if (!byUnit.has(uid)) byUnit.set(uid, { officers: 0, jcos: 0, ors: 0 });
      return byUnit.get(uid)!;
    };
    fbs.forEach((fb) => {
      const r = ensure(fb.unitId);
      r.officers += fb.manpowerOfficers ?? 0;
      r.jcos += fb.manpowerJCOs ?? 0;
      r.ors += fb.manpowerORs ?? 0;
    });
    const rows = Array.from(byUnit.entries()).map(([unitId, v]) => ({
      unitId,
      code: units.find((u) => u.id === unitId)?.code ?? unitId?.slice(0, 6) ?? '—',
      ...v,
      friendly: v.officers + v.jcos + v.ors,
    }));
    const friendlyTotal = rows.reduce(
      (acc, r) => ({
        officers: acc.officers + r.officers,
        jcos: acc.jcos + r.jcos,
        ors: acc.ors + r.ors,
        total: acc.total + r.friendly,
      }),
      { officers: 0, jcos: 0, ors: 0, total: 0 }
    );
    // Enemy manpower over ALL targets (incl. global unitId:null), eliminated = ELIMINATED sum (§19.2).
    let total = 0;
    let eliminated = 0;
    targets.forEach((t) => {
      const m = t.enemyManpower ?? 0;
      total += m;
      if (t.status === 'ELIMINATED') eliminated += m;
    });
    return { rows, friendlyTotal, enemy: { total, eliminated, remaining: total - eliminated } };
  }, [dispoQ.data, farmbasesQ.data, targets, units]);

  return (
    <div className="rounded-xl border border-command-border bg-command-panel p-4">
      <h3 className="text-sm font-semibold text-gray-100 mb-3">Force Disposition</h3>
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="text-gray-400 text-[10px] uppercase border-b border-command-border">
              <th className="text-left py-1 px-2">Unit</th>
              <th className="text-center py-1 px-2">Off</th>
              <th className="text-center py-1 px-2">JCO</th>
              <th className="text-center py-1 px-2">OR</th>
              <th className="text-center py-1 px-2 text-cyan-400">Friendly</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.unitId} className="border-b border-command-border/50">
                <td className="py-1 px-2 text-gray-200 font-semibold">{r.code}</td>
                <td className="py-1 px-2 text-center text-gray-300">{r.officers}</td>
                <td className="py-1 px-2 text-center text-gray-300">{r.jcos}</td>
                <td className="py-1 px-2 text-center text-gray-300">{r.ors}</td>
                <td className="py-1 px-2 text-center text-cyan-300 font-semibold">{r.friendly}</td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr><td colSpan={5} className="py-6 text-center text-gray-500">No disposition data yet.</td></tr>
            )}
          </tbody>
          {rows.length > 0 && (
            <tfoot>
              <tr className="border-t border-command-border text-gray-200 font-semibold">
                <td className="py-1 px-2">Total</td>
                <td className="py-1 px-2 text-center">{friendlyTotal.officers}</td>
                <td className="py-1 px-2 text-center">{friendlyTotal.jcos}</td>
                <td className="py-1 px-2 text-center">{friendlyTotal.ors}</td>
                <td className="py-1 px-2 text-center text-cyan-300">{friendlyTotal.total}</td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>

      {/* Enemy manpower summary (§19.2 enemy.totals — spans all targets incl. global). */}
      <div className="grid grid-cols-3 gap-2 mt-3">
        <div className="rounded-lg border border-red-500/30 bg-command-bg/40 p-2 text-center">
          <div className="text-base font-display font-bold text-red-300">{enemy.total}</div>
          <div className="text-[9px] text-gray-400 uppercase tracking-wide">Enemy total</div>
        </div>
        <div className="rounded-lg border border-green-500/30 bg-command-bg/40 p-2 text-center">
          <div className="text-base font-display font-bold text-green-400">{enemy.eliminated}</div>
          <div className="text-[9px] text-gray-400 uppercase tracking-wide">Eliminated</div>
        </div>
        <div className="rounded-lg border border-command-border bg-command-bg/40 p-2 text-center">
          <div className="text-base font-display font-bold text-gray-200">{enemy.remaining}</div>
          <div className="text-[9px] text-gray-400 uppercase tracking-wide">Remaining</div>
        </div>
      </div>
    </div>
  );
}
